import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Star, AlertCircle, Save } from "lucide-react";
import {
    formatCodePostal, isValidCodePostal,
    formatTelephone, isValidTelephone,
    isValidEmail,
} from "./formatters";
import { Row, compactInputCls, compactInputMonoCls } from "./RowLayout";
import { VilleCombobox } from "./VilleCombobox";
import { PROVINCES_CA } from "./provinces_ca";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const FieldError = ({ children, testId }) => (
    <p className="text-[10px] text-red-600 mt-0.5" data-testid={testId}>{children}</p>
);

// Validation : retourne true si l'adresse contient le minimum requis pour
// être persistée (POST initial ou UPDATE).
const isAdresseSavable = (adr) => {
    if (!adr) return false;
    if (!(adr.address || "").trim()) return false;
    if (!(adr.ville || "").trim()) return false;
    if (!isValidCodePostal(adr.codepostal)) return false;
    if (!(adr.email || "").trim()) return false;
    if (!isValidEmail(adr.email)) return false;
    if (!isValidTelephone(adr.telephone)) return false;
    if (!isValidTelephone(adr.telephone2)) return false;
    if (!isValidTelephone(adr.fax)) return false;
    return true;
};

// Erreurs d'affichage (recalculées à chaque render — pas besoin de state)
const computeErrors = (adr) => {
    const e = {};
    if (!adr) return e;
    if (!(adr.address || "").trim()) e.address = "L'adresse est obligatoire";
    if (!(adr.ville || "").trim()) e.ville = "La ville est obligatoire";
    if (!isValidCodePostal(adr.codepostal)) e.codepostal = "Format requis : A1A 1A1";
    if (!isValidTelephone(adr.telephone)) e.telephone = "Format requis : 514-555-1234 (10 chiffres)";
    if (!isValidTelephone(adr.telephone2)) e.telephone2 = "Format requis : 514-555-1234 (10 chiffres)";
    if (!isValidTelephone(adr.fax)) e.fax = "Format requis : 514-555-1234 (10 chiffres)";
    if (!(adr.email || "").trim()) e.email = "Le courriel est obligatoire";
    else if (!isValidEmail(adr.email)) e.email = "Adresse courriel invalide";
    return e;
};

export const AdressesTab = ({
    readOnly, adresses, editAdr, setEditAdr,
    onDelete,
    onSelectAdresse, onNewAdresse, onCancelNew, onSaveAdresse,
    isDirty, saving,
}) => {
    // Dialog confirmant le changement d'adresse courante
    const [confirm, setConfirm] = useState(null);

    const setField = (key, value) => setEditAdr({ ...editAdr, [key]: value });

    // Toggle « Adresse courante » : déclenche le dialog si conflit, sinon applique directement
    const handleToggleCourante = (nextValue) => {
        const existingCourante = adresses.find((a) => a.courant && a.id !== editAdr?.id);
        if (nextValue && existingCourante) {
            setConfirm({ type: "replace", existing: existingCourante });
            return;
        }
        setField("courant", nextValue);
    };

    const errors = computeErrors(editAdr);

    const confirmYes = () => {
        if (confirm?.type === "replace") {
            setField("courant", true);
        }
        setConfirm(null);
    };
    const confirmNo = () => setConfirm(null);

    return (
        <div className="space-y-4">
            {/* === PANNEAU D'ÉDITION (toujours visible) === */}
            {editAdr ? (
                <div className="border-2 border-[#0033A0] rounded-md p-4 space-y-3 bg-blue-50/30">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="font-semibold text-sm flex items-center gap-2">
                            {editAdr.id ? "Modifier l'adresse" : "Nouvelle adresse"}
                            {editAdr.courant && (
                                <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">
                                    <Star size={10} className="mr-1" />Courante
                                </Badge>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            {isDirty && (
                                <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 gap-1" data-testid="adr-dirty-badge">
                                    <AlertCircle size={11} />
                                    Modifié
                                </Badge>
                            )}
                            {/* En mode « Nouvelle adresse » uniquement : bouton Annuler
                                pour revenir sur la courante sans créer la nouvelle. */}
                            {!editAdr.id && adresses.length > 0 && (
                                <Button
                                    variant="outline" size="sm"
                                    onClick={onCancelNew}
                                    className="rounded-md"
                                    data-testid="close-adresse-btn"
                                    disabled={saving}
                                >
                                    Annuler
                                </Button>
                            )}
                            {!readOnly && (
                                <Button
                                    size="sm"
                                    onClick={onSaveAdresse}
                                    disabled={!isDirty || !isAdresseSavable(editAdr) || saving}
                                    className="rounded-md bg-[#0033A0] hover:bg-[#002277] text-white disabled:bg-slate-200 disabled:text-slate-500"
                                    data-testid="save-adresse-btn"
                                >
                                    <Save size={14} className="mr-2" />
                                    {saving
                                        ? "Enregistrement…"
                                        : editAdr.id ? "Sauvegarder" : "Créer l'adresse"}
                                </Button>
                            )}
                        </div>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8">
                        {/* === COLONNE GAUCHE — adresse postale === */}
                        <div>
                            <Row label="Adresse" required>
                                <Input
                                    value={editAdr.address || ""}
                                    onChange={(e) => setField("address", e.target.value)}
                                    className={`${compactInputCls} ${errors.address ? "border-red-500" : ""}`}
                                    data-testid="adr-address"
                                />
                                {errors.address && <FieldError testId="err-address">{errors.address}</FieldError>}
                            </Row>
                            <Row label="Adresse 2">
                                <Input
                                    value={editAdr.adresse2 || ""}
                                    onChange={(e) => setField("adresse2", e.target.value)}
                                    className={compactInputCls}
                                    maxLength={30}
                                    data-testid="adr-adresse2"
                                />
                            </Row>
                            <Row label="Adresse 3">
                                <Input
                                    value={editAdr.adresse3 || ""}
                                    onChange={(e) => setField("adresse3", e.target.value)}
                                    className={compactInputCls}
                                    maxLength={30}
                                    data-testid="adr-adresse3"
                                />
                            </Row>
                            <Row label="Ville" required>
                                <VilleCombobox
                                    value={editAdr.ville || ""}
                                    onChange={(v) => setField("ville", v)}
                                    disabled={readOnly}
                                    className={`${compactInputCls} ${errors.ville ? "border-red-500" : ""}`}
                                    testId="adr-ville"
                                />
                                {errors.ville && <FieldError testId="err-ville">{errors.ville}</FieldError>}
                            </Row>
                            <Row label="Province">
                                <Select
                                    value={editAdr.province || "QC"}
                                    onValueChange={(v) => setField("province", v)}
                                    disabled={readOnly}
                                >
                                    <SelectTrigger className={`${compactInputMonoCls}`} data-testid="adr-province">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {PROVINCES_CA.map((p) => (
                                            <SelectItem key={p.code} value={p.code} className="text-sm">
                                                <span className="font-mono mr-2">{p.code}</span>
                                                <span className="text-slate-600">— {p.nom}</span>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </Row>
                            <Row label="Code postal" required hint="Format A1A 1A1">
                                <Input
                                    value={editAdr.codepostal || ""}
                                    onChange={(e) => setField("codepostal", formatCodePostal(e.target.value))}
                                    maxLength={7}
                                    placeholder="A1A 1A1"
                                    className={`${compactInputMonoCls} uppercase ${errors.codepostal ? "border-red-500" : ""}`}
                                    data-testid="adr-codepostal"
                                />
                                {errors.codepostal && <FieldError testId="err-codepostal">{errors.codepostal}</FieldError>}
                            </Row>
                            <Row label="Courriel" required>
                                <Input
                                    type="email"
                                    value={editAdr.email || ""}
                                    onChange={(e) => setField("email", e.target.value)}
                                    placeholder="exemple@domaine.com"
                                    className={`${compactInputCls} ${errors.email ? "border-red-500" : ""}`}
                                    data-testid="adr-email"
                                />
                                {errors.email && <FieldError testId="err-email">{errors.email}</FieldError>}
                            </Row>
                        </div>

                        {/* === COLONNE DROITE — coordonnées téléphoniques === */}
                        <div>
                            <Row label="Téléphone">
                                <div className="flex items-stretch gap-2">
                                    <Input
                                        value={editAdr.telephone || ""}
                                        onChange={(e) => setField("telephone", formatTelephone(e.target.value))}
                                        inputMode="numeric"
                                        placeholder="514-555-1234"
                                        maxLength={12}
                                        className={`${compactInputMonoCls} flex-1 ${errors.telephone ? "border-red-500" : ""}`}
                                        data-testid="adr-telephone"
                                    />
                                    <Input
                                        value={editAdr.poste1 || ""}
                                        onChange={(e) => setField("poste1", e.target.value.replace(/\D/g, "").slice(0, 6))}
                                        inputMode="numeric"
                                        placeholder="Poste"
                                        maxLength={6}
                                        title="Poste téléphonique"
                                        className={`${compactInputMonoCls} w-24`}
                                        data-testid="adr-poste1"
                                    />
                                </div>
                                {errors.telephone && <FieldError testId="err-telephone">{errors.telephone}</FieldError>}
                            </Row>
                            <Row label="Téléphone 2">
                                <div className="flex items-stretch gap-2">
                                    <Input
                                        value={editAdr.telephone2 || ""}
                                        onChange={(e) => setField("telephone2", formatTelephone(e.target.value))}
                                        inputMode="numeric"
                                        placeholder="514-555-1234"
                                        maxLength={12}
                                        className={`${compactInputMonoCls} flex-1 ${errors.telephone2 ? "border-red-500" : ""}`}
                                    />
                                    <Input
                                        value={editAdr.poste2 || ""}
                                        onChange={(e) => setField("poste2", e.target.value.replace(/\D/g, "").slice(0, 6))}
                                        inputMode="numeric"
                                        placeholder="Poste"
                                        maxLength={6}
                                        title="Poste téléphonique"
                                        className={`${compactInputMonoCls} w-24`}
                                        data-testid="adr-poste2"
                                    />
                                </div>
                                {errors.telephone2 && <FieldError>{errors.telephone2}</FieldError>}
                            </Row>
                            <Row label="Télécopieur">
                                <Input
                                    value={editAdr.fax || ""}
                                    onChange={(e) => setField("fax", formatTelephone(e.target.value))}
                                    inputMode="numeric"
                                    placeholder="514-555-1234"
                                    maxLength={12}
                                    className={`${compactInputMonoCls} ${errors.fax ? "border-red-500" : ""}`}
                                />
                                {errors.fax && <FieldError>{errors.fax}</FieldError>}
                            </Row>
                            <Row label="Adresse courante">
                                <div className="flex items-center h-8">
                                    <Switch
                                        checked={!!editAdr.courant}
                                        onCheckedChange={handleToggleCourante}
                                        data-testid="adr-courant-switch"
                                    />
                                </div>
                            </Row>
                        </div>
                    </div>
                    {!isAdresseSavable(editAdr) && (
                        <p className="text-xs text-amber-700 italic">
                            &#9888; Tous les champs requis (adresse, ville, code postal, courriel) doivent être renseignés et valides pour pouvoir enregistrer.
                        </p>
                    )}
                    {isAdresseSavable(editAdr) && isDirty && (
                        <p className="text-xs text-slate-500 italic">
                            Cliquez sur « Sauvegarder » dans cet encadré pour enregistrer cette adresse.
                        </p>
                    )}
                </div>
            ) : null}

            {/* === BOUTON « Nouvelle adresse » + LISTE COMPLÈTE === */}
            {!readOnly && (
                <div className="flex items-center justify-between">
                    <div className="text-[12px] uppercase font-bold tracking-wide text-slate-700">
                        {adresses.length > 0
                            ? `Toutes les adresses (${adresses.length})`
                            : "Aucune adresse"}
                    </div>
                    <Button
                        onClick={onNewAdresse}
                        variant="outline"
                        size="sm"
                        className="rounded-md"
                        data-testid="add-adresse-btn"
                    >
                        <Plus size={14} className="mr-2" /> Nouvelle adresse
                    </Button>
                </div>
            )}

            <div className="space-y-2">
                {adresses.map((adr) => {
                    const isSelected = !!editAdr?.id && editAdr.id === adr.id;
                    return (
                        <div
                            key={adr.id}
                            className={`rounded-md p-3 flex items-start justify-between cursor-pointer transition-colors ${
                                isSelected
                                    ? "border-2 border-[#0033A0] bg-blue-50 ring-2 ring-[#0033A0]/20"
                                    : "border border-slate-200 hover:border-[#0033A0] hover:bg-slate-50"
                            }`}
                            onClick={() => onSelectAdresse(adr)}
                            data-testid={`adresse-row-${adr.id}`}
                            aria-current={isSelected ? "true" : undefined}
                        >
                            <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                    {adr.courant && <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100"><Star size={10} className="mr-1" />Courante</Badge>}
                                    {isSelected && (
                                        <Badge className="bg-[#0033A0] text-white hover:bg-[#0033A0]">
                                            En modification
                                        </Badge>
                                    )}
                                    <span className="font-medium text-slate-900">{adr.address || "—"}</span>
                                </div>
                                <div className="text-xs text-slate-600">
                                    {[adr.ville, adr.province, adr.codepostal].filter(Boolean).join(", ")}
                                    {adr.telephone && ` • ${adr.telephone}`}
                                    {adr.email && ` • ${adr.email}`}
                                </div>
                            </div>
                            {!readOnly && (
                                <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                                    <Button variant="ghost" size="icon" onClick={() => onSelectAdresse(adr)} title="Modifier"><Pencil size={14} /></Button>
                                    <Button variant="ghost" size="icon" className="text-red-600" onClick={() => onDelete(adr)} title="Supprimer"><Trash2 size={14} /></Button>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            <AlertDialog open={confirm !== null} onOpenChange={(o) => !o && setConfirm(null)}>
                <AlertDialogContent data-testid="adr-confirm-dialog">
                    {confirm?.type === "replace" && (
                        <>
                            <AlertDialogHeader>
                                <AlertDialogTitle>Remplacer l&apos;adresse courante ?</AlertDialogTitle>
                                <AlertDialogDescription>
                                    L&apos;adresse <span className="font-semibold">{confirm.existing?.address || "—"}</span>
                                    {confirm.existing?.ville ? `, ${confirm.existing.ville}` : ""} est actuellement définie comme courante.
                                    En enregistrant cette adresse comme courante, l&apos;autre cessera de l&apos;être.
                                    Souhaitez-vous continuer ?
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel onClick={confirmNo} data-testid="adr-confirm-no">Non, garder l&apos;autre comme courante</AlertDialogCancel>
                                <AlertDialogAction onClick={confirmYes} data-testid="adr-confirm-yes" className="bg-[#0033A0] hover:bg-[#002277]">Oui, remplacer</AlertDialogAction>
                            </AlertDialogFooter>
                        </>
                    )}
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};
